import { useEffect, useState, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { get, isEmpty } from 'lodash'
import { useSelector, useDispatch } from 'react-redux'
import { withRouter } from 'next/router'
import * as Cookies from 'js-cookie'

import {
  attemptLogin,
  clearAuthentication,
} from 'redux-web/utils/authentication/actions'
import { setPhoneNumber } from 'redux-web/utils/payBySignage/actions'
import {
  removeGuestSession,
  getGuestSession,
  setGuestSession,
} from 'helpers/guest'
import {
  getReservationSession,
  removeReservationSession,
} from 'helpers/reservation'
import {
  nationalPhoneNumber,
  internationalPhoneNumber,
} from 'helpers/phoneFormat'
import { showBasicModal } from 'helpers/modal'
import { useTranslation } from 'i18n-web/i18next'
import { getSelectedRateName } from 'models/payBySignage'
import API from 'api'

import SignInForm from './Screen'

const getLoginType = data => {
  let loggedBy = 'password'

  if (data.isFacebookConnected) {
    loggedBy = 'facebook'
  } else if (data.isAppleConnected) {
    loggedBy = 'apple'
  }

  return loggedBy
}

const FormikHOC = props => {
  const { isQRCode, lotId, rates, signInRef, setShakeButton, hasNextButton } =
    props

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const error = useSelector(state => state.authentication.error)
  const country = useSelector(state => state.countries.country)
  const { flow, phoneNumber, selectedRate } = useSelector(
    state => state.payBySignage
  )

  const [enableToSubmit, setEnableToSubmit] = useState(true)
  const [isValid, setIsValid] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [enableLogin, setEnableLogin] = useState(!isQRCode)
  const [loginType, setLoginType] = useState(get(flow, 'loginType', null))

  const currentCountry = useMemo(
    () => phoneNumber.countryCode || country,
    [phoneNumber, country]
  )

  const passwordRef = useRef()

  const initialValues = {
    password: '',
    phoneNumber: internationalPhoneNumber(phoneNumber.number, currentCountry),
  }

  const onPhoneNumberSubmit = async (phoneNumber, setSubmitting, setStatus) => {
    await API()
      .getPhoneNumber(currentCountry, phoneNumber, {
        timeoutTryAgainMessage: true,
      })
      .then(resp => {
        if (!resp.error) {
          const guestSession = getGuestSession()
          const reservationSession = getReservationSession()

          dispatch(
            setPhoneNumber({
              number: phoneNumber,
              countryCode: currentCountry,
            })
          )

          const phoneNumberStatus = resp.phoneNumber

          setSubmitting(false)
          setEnableLogin(true)
          if (phoneNumberStatus.userExists) {
            if (guestSession) removeGuestSession()
            if (reservationSession) removeReservationSession()

            setLoginType(getLoginType(phoneNumberStatus))
          } else {
            if (!guestSession) setGuestSession()
            if (reservationSession) removeReservationSession()
          }
        } else {
          setSubmitting(false)
          if (resp.error.code === 408) {
            dispatch(showBasicModal({ title: resp.error.message }))
          }
        }
      })
      .catch(err => {
        setStatus({ message: err })
        setSubmitting(false)
      })
  }

  const resetFormState = () => {
    if (hasNextButton) {
      setShakeButton(false)
    }
    if (!hasNextButton && !isValid) {
      setEnableToSubmit(true)
    }
    if (isQRCode) {
      setEnableLogin(false)
      setLoginType(null)
      if (!isEmpty(phoneNumber.number)) {
        dispatch(setPhoneNumber({}))
        if (passwordRef.current) {
          passwordRef.current.value = ''
        }
      }
    } else {
      setEnableLogin(false)
      setLoginType(null)
    }
  }

  const onAppleButtonClick = () => {
    const selectedRateName = getSelectedRateName(rates, selectedRate)

    localStorage.setItem(`selected-rate-lot-${lotId}`, selectedRateName)
  }

  const handleSubmit = (
    values,
    { setSubmitting, setStatus, setTouched, setFieldValue }
  ) => {
    setSubmitting(true)

    const phoneNumber = nationalPhoneNumber(values.phoneNumber, currentCountry)

    if (
      (values.phoneNumber && !values.password && !loginType) ||
      !enableLogin
    ) {
      onPhoneNumberSubmit(phoneNumber, setSubmitting, setStatus)
      setTouched({ password: false })
    } else if (values.password !== '') {
      dispatch(
        attemptLogin({
          data: {
            phoneCountryCode: currentCountry,
            ...values,
            phoneNumber,
          },
        })
      )
    } else {
      setSubmitting(false)
    }

    setFieldValue('password', '')
  }

  const onClearAuthentication = () => dispatch(clearAuthentication())

  const onFocusField = (setSubmitting, setStatus) => {
    onClearAuthentication()
    setSubmitting(false)
    setStatus(null)
  }

  const submitOnKeyUp = (isValid, isSubmitting) => {
    if (!hasNextButton) {
      setIsValid(isValid)
      setIsSubmitting(isSubmitting)
    }
  }

  useEffect(() => {
    if (isValid && signInRef && !isSubmitting && enableToSubmit) {
      signInRef.current.submitForm()
      setEnableToSubmit(false)
    }
  }, [isValid, isSubmitting, enableToSubmit])

  useEffect(() => {
    const authenticationToken = Cookies.get('appleAuthToken')

    // Apple login redirection
    if (authenticationToken) {
      dispatch(
        attemptLogin({
          data: {
            authenticationToken,
          },
        })
      )
    }
  }, [])

  useEffect(() => {
    if (phoneNumber.number) {
      signInRef.current.setFieldValue(
        'phoneNumber',
        internationalPhoneNumber(phoneNumber.number, currentCountry)
      )
    }
  }, [phoneNumber])

  useEffect(() => {
    if (error) {
      dispatch(
        showBasicModal({
          title: error,
        })
      )
    }
  }, [error])

  useEffect(() => {
    resetFormState()
  }, [country])

  useEffect(() => {
    if (flow) setLoginType(get(flow, 'loginType'))
  }, [flow])

  return (
    <SignInForm
      error={error}
      handleSubmit={handleSubmit}
      onAppleButtonClick={onAppleButtonClick}
      initialValues={initialValues}
      onClearAuthentication={onClearAuthentication}
      onFocusField={onFocusField}
      passwordRef={passwordRef}
      resetFormState={resetFormState}
      currentCountry={currentCountry}
      loginType={loginType}
      enableLogin={enableLogin}
      submitOnKeyUp={submitOnKeyUp}
      signInRef={signInRef}
      hasNextButton={hasNextButton}
      t={t}
      {...props}
    />
  )
}

FormikHOC.propTypes = {
  router: PropTypes.object,
  rates: PropTypes.array,
  isQRCode: PropTypes.bool,
  phoneNumberRef: PropTypes.object,
  signInRef: PropTypes.object,
  lotId: PropTypes.number,
  setShakeButton: PropTypes.func,
  hasNextButton: PropTypes.bool,
}

export default withRouter(FormikHOC)
